<template>
  <div>
    <div class="flex justify-between items-center mb-2">
      <p class="text-sm font-semibold text-gray-700">Add Crew Members</p>
      <Checkbox
        v-model="showAll"
        label="View all Crew Members"
        class="text-sm"
      />
    </div>
    <div class="flex gap-2 items-start">
      <SelectInput
        v-model="selectedCrew"
        placeholder="Search and Select to Add Crew Members"
        :options="filteredOptions"
        filter
        filter-placeholder="Search crew members..."
        class="flex-1"
      >
        <template #option="{ option }">
          <div class="flex items-center">
            <DepartmentIcon
              v-if="option.value && !isSameDepartment(option)"
              :department="option.department"
            />
            <span class="ml-1" :class="{ 'line-through': option.archived }">{{
              option.label
            }}</span>
          </div>
        </template>
      </SelectInput>
      <Button
        label="Add"
        :disabled="!selectedCrew"
        size="medium"
        @click="addCrewMember"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import DepartmentIcon from "@/components/DepartmentIcon.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";

interface CrewOption {
  id: number;
  full_name: string;
  label: string;
  value: number;
  department: { id: number; name?: string };
  archived?: boolean;
  [key: string]: unknown;
}

const props = defineProps<{
  crewMembers: unknown[];
  fetchLink: Record<string, unknown>;
}>();

const emit = defineEmits<{
  "add-crew": [member: CrewOption];
}>();

const store = useStore();
const showAll = ref(false);
const selectedCrew = ref<number | null>(null);
const options = ref<CrewOption[]>([]);

const departmentId = computed(() => {
  const job = (store.state as Record<string, unknown>).job as
    | Record<string, unknown>
    | undefined;
  const dept = job?.department as { id: number } | undefined;
  return dept?.id ?? -1;
});

const existingIds = computed(() => {
  const members = props.crewMembers as Array<{ id?: number }>;
  return new Set(members.map((m) => m.id));
});

const filteredOptions = computed(() => {
  const base = showAll.value
    ? options.value
    : options.value.filter((opt) => opt.department?.id === departmentId.value);
  return base.filter((opt) => !existingIds.value.has(opt.value));
});

const isSameDepartment = (option: CrewOption): boolean =>
  option.department ? option.department.id === departmentId.value : false;

const parseToOptions = (data: Array<Record<string, unknown>>): CrewOption[] =>
  data.map((member) => ({
    ...member,
    label: member.full_name as string,
    value: member.id as number
  })) as CrewOption[];

const fetchCrewOptions = () => {
  const fromStore = (store.state as Record<string, unknown>)
    .crewMembers as Array<Record<string, unknown>>;
  if (fromStore && fromStore.length > 0) {
    options.value = parseToOptions(fromStore);
    return;
  }
  store
    .dispatch("fetchCrewMembers", { link: props.fetchLink })
    .then((res: { data: Array<Record<string, unknown>> }) => {
      options.value = parseToOptions(res.data);
    });
};

const addCrewMember = () => {
  const selected = options.value.find(
    (opt) => opt.value === selectedCrew.value
  );
  if (selected) {
    emit("add-crew", selected);
    selectedCrew.value = null;
  }
};

onMounted(fetchCrewOptions);
</script>
